import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Eye, EyeOff, Mail, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [dest, setDest] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function requestCode(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setDest(data.destination || "");
      setStep(2);
      toast.success("Reset code sent");
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  }

  async function verifyCode(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { identifier: identifier.trim(), otp: otp.trim() });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  }

  async function setNewPassword(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: password });
      setStep(4);
      toast.success("Password updated");
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  }

  return (
    <div className="min-h-screen brand-grid-bg flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="back-to-login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1A2A5E] mb-5">
          <ArrowLeft className="h-4 w-4" /> Back to Login
        </Link>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-slate-50 rounded-xl p-1.5 ring-1 ring-slate-100"><Logo size={40} /></div>
              <div>
                <div className="font-display font-extrabold text-lg leading-none"><span className="text-[#1A2A5E]">Bill</span><span className="text-[#F4571E]">One</span></div>
                <div className="text-[8px] tracking-[0.2em] text-slate-400 font-semibold mt-1">RESET YOUR PASSWORD</div>
              </div>
            </div>

            {step === 1 && (
              <form onSubmit={requestCode} className="space-y-4" data-testid="forgot-step-request">
                <div className="flex items-center gap-3 mb-1">
                  <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-[#1A2A5E]/10"><Mail className="h-6 w-6 text-[#1A2A5E]" /></div>
                  <div>
                    <h1 className="font-display text-xl font-bold text-[#0F172A]">Forgot Password</h1>
                    <p className="text-xs text-slate-400">We'll send a 6-digit code to your email</p>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600">Email or Username</Label>
                  <Input data-testid="forgot-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@store.com" className="mt-1.5" autoFocus required />
                </div>
                {error && <div data-testid="forgot-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                <Button type="submit" data-testid="forgot-send-code" disabled={loading} className="w-full h-11 text-base font-semibold bg-[#1A2A5E]">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : "Send Reset Code"}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={verifyCode} className="space-y-4" data-testid="forgot-step-verify">
                <div className="flex items-center gap-3 mb-1">
                  <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-[#F4571E]/15"><KeyRound className="h-6 w-6 text-[#F4571E]" /></div>
                  <div>
                    <h1 className="font-display text-xl font-bold text-[#0F172A]">Enter Code</h1>
                    <p className="text-xs text-slate-400">Sent to {dest || "your email"}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600">6-digit Code</Label>
                  <Input data-testid="forgot-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="______" className="mt-1.5 tracking-[0.5em] text-center text-lg font-bold" inputMode="numeric" autoFocus required />
                </div>
                {error && <div data-testid="forgot-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                <Button type="submit" data-testid="forgot-verify-code" disabled={loading || otp.length < 6} className="w-full h-11 text-base font-semibold bg-[#1A2A5E]">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</> : "Verify Code"}
                </Button>
                <button type="button" onClick={requestCode} disabled={loading} className="w-full text-sm text-[#F4571E] font-semibold hover:underline" data-testid="forgot-resend">Resend Code</button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={setNewPassword} className="space-y-4" data-testid="forgot-step-reset">
                <div className="flex items-center gap-3 mb-1">
                  <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-[#1A2A5E]/10"><KeyRound className="h-6 w-6 text-[#1A2A5E]" /></div>
                  <div>
                    <h1 className="font-display text-xl font-bold text-[#0F172A]">New Password</h1>
                    <p className="text-xs text-slate-400">Choose a strong password (min 6 chars)</p>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600">New Password</Label>
                  <div className="relative mt-1.5">
                    <Input data-testid="forgot-new-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <div data-testid="forgot-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                <Button type="submit" data-testid="forgot-set-password" disabled={loading} className="w-full h-11 text-base font-semibold bg-[#F4571E]">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating...</> : "Update Password"}
                </Button>
              </form>
            )}

            {step === 4 && (
              <div className="text-center py-6" data-testid="forgot-step-done">
                <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-4" />
                <h1 className="font-display text-xl font-bold text-[#0F172A]">Password Updated</h1>
                <p className="text-sm text-slate-500 mt-1.5">You can now sign in with your new password.</p>
                <Button data-testid="forgot-goto-login" onClick={() => navigate("/", { replace: true })} className="mt-6 w-full h-11 text-base font-semibold bg-[#1A2A5E]">Go to Login</Button>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Powered by <span className="font-semibold text-slate-500">ACore</span></p>
      </div>
    </div>
  );
}
